// CollectionImport — the import half of "your data is yours" (profile page,
// COLLECTION STATS card). Upload → preview → confirm → done/undo, per the
// approved mockup. All merge decisions live in lib/collectionImport.mjs
// (pure, node --test covered); supabase/SheetJS wiring in
// lib/collectionImportRun.js. Photos and marketplace state never change here.

import React, { useState, useRef, useCallback } from 'react';
import {
  parseForImport, classifySession, ensureSetLoaded, patchSheetRow,
  runImport, retryImport, undoImport, downloadImportTemplate,
} from '../lib/collectionImportRun';
import { VALID_STATUSES, parallelsForSubset } from '../lib/collectionImport.mjs';
import { SET_REGISTRY } from '../setRegistry';

const fmtVal = (v) => (v === null || v === undefined || v === '' ? '(empty)' : String(v));
const errId = (e) => `${e.sheet} ${e.rowNum}`;

export default function CollectionImport({ user, onDataChanged }) {
  // idle → entry → parsing → preview → writing → done
  const [stage, setStage] = useState('idle');
  const [plan, setPlan] = useState(null);
  const sessionRef = useRef(null); // parsed sheets + existing rows + set data
  const [fixDrafts, setFixDrafts] = useState({}); // errId -> { setId, subId, cardN, parallel }
  const [progress, setProgress] = useState({ written: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [chunkFail, setChunkFail] = useState(null);
  const [error, setError] = useState('');
  const [undoState, setUndoState] = useState(null); // null | 'undoing' | { deleted }
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setStage('idle'); setPlan(null); setChunkFail(null); setError('');
    setProgress({ written: 0, total: 0 });
    setFixDrafts({}); sessionRef.current = null;
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const onFile = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setStage('parsing');
    setError('');
    try {
      const session = await parseForImport(user, file);
      sessionRef.current = session;
      setPlan(classifySession(session));
      setFixDrafts({});
      setStage('preview');
    } catch (err) {
      console.error('Import parse failed:', err);
      setError(`Couldn't read that file — ${err.message || 'is it a .xlsx or .csv?'}`);
      setStage('entry');
    }
    if (fileRef.current) fileRef.current.value = '';
  }, [user]);

  // ── In-preview fixes: patch the parsed sheet, re-classify, no re-upload ──
  const setDraft = useCallback((id, patch) => {
    setFixDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  }, []);

  const pickSet = useCallback(async (id, setId) => {
    setDraft(id, { setId, subId: '', cardN: '', parallel: '', loading: !!setId });
    if (setId) {
      await ensureSetLoaded(sessionRef.current, setId);
      setDraft(id, { loading: false });
    }
  }, [setDraft]);

  const applyFix = useCallback((err) => {
    const d = fixDrafts[errId(err)];
    if (!d || !d.setId || !d.subId || !d.cardN) return;
    patchSheetRow(sessionRef.current, err.sheet, err.rowNum, {
      'Set ID': d.setId,
      'Set Name': '',
      'Subset': d.subId,
      'Card #': d.cardN,
      'Parallel': d.parallel || '',
      'Card Key': '',
    });
    setPlan(classifySession(sessionRef.current));
    setFixDrafts((prev) => { const n = { ...prev }; delete n[errId(err)]; return n; });
  }, [fixDrafts]);

  const applyStatusFix = useCallback((err, status) => {
    if (!status) return;
    patchSheetRow(sessionRef.current, err.sheet, err.rowNum, { 'Status': status });
    setPlan(classifySession(sessionRef.current));
  }, []);

  const confirm = useCallback(async () => {
    setStage('writing');
    setChunkFail(null);
    setProgress({ written: 0, total: plan.inserts.length + plan.updates.length });
    try {
      const res = await runImport(user, plan, (written, total) => setProgress({ written, total }));
      setResult(res);
      setUndoState(null);
      setStage('done');
      if (onDataChanged) onDataChanged();
    } catch (err) {
      if (err.remainingChunks) setChunkFail(err);
      else { setError(`Import failed — ${err.message || 'something went wrong'}`); setStage('preview'); }
    }
  }, [user, plan, onDataChanged]);

  const retry = useCallback(async () => {
    const failed = chunkFail;
    setChunkFail(null);
    try {
      await retryImport(failed, (written, total) => setProgress({ written, total }));
      setResult({
        batchId: failed.batchId,
        newKeys: plan.inserts.map((r) => r.card_key),
        newCount: plan.inserts.length,
        updateCount: plan.updates.length,
        errorCount: plan.counts.error,
      });
      setUndoState(null);
      setStage('done');
      if (onDataChanged) onDataChanged();
    } catch (err) {
      if (err.remainingChunks) setChunkFail(err);
      else setError(`Import failed — ${err.message || 'something went wrong'}`);
    }
  }, [chunkFail, plan, onDataChanged]);

  const undo = useCallback(async () => {
    if (!result || undoState) return;
    setUndoState('undoing');
    try {
      const { deleted } = await undoImport(user, result.batchId, result.newKeys);
      setUndoState({ deleted });
      if (onDataChanged) onDataChanged();
    } catch (err) {
      setUndoState(null);
      setError(err.message || "couldn't undo the import — try again");
    }
  }, [user, result, undoState, onDataChanged]);

  const c = plan?.counts;
  const writeCount = plan ? plan.inserts.length + plan.updates.length : 0;
  const sampleChanges = plan
    ? plan.rows.filter((r) => r.kind === 'update').slice(0, 6)
    : [];

  return (
    <div>
      <style>{CSS}</style>
      <input ref={fileRef} type="file" accept=".csv,.xlsx" style={{ display: 'none' }} onChange={onFile} />

      {/* strip content (below the export buttons) */}
      <div className="cim-sep" />
      {stage === 'done' && result && (
        <div className="cim-done">
          {undoState && undoState.deleted !== undefined ? (
            <span>↩ UNDO COMPLETE — {undoState.deleted} imported card{undoState.deleted === 1 ? '' : 's'} removed. Edits to existing cards were kept.</span>
          ) : (
            <>
              <span>
                ✓ IMPORTED — {result.newCount} new card{result.newCount === 1 ? '' : 's'} added, {result.updateCount} updated
                {result.errorCount ? `, ${result.errorCount} row${result.errorCount === 1 ? '' : 's'} skipped (errors)` : ''}
              </span>
              {result.newCount > 0 && (
                <button className="cim-btn cim-btn-danger" onClick={undo} disabled={undoState === 'undoing'}>
                  {undoState === 'undoing' ? '◐ UNDOING…' : '↩ UNDO THIS IMPORT'}
                </button>
              )}
            </>
          )}
        </div>
      )}
      {stage === 'done' && result && !undoState && result.newCount > 0 && (
        <div className="cim-hint" style={{ marginTop: 8 }}>
          Undo removes the {result.newCount} newly added card{result.newCount === 1 ? '' : 's'}. Edits to existing cards stay. Available until you leave this page.
        </div>
      )}
      {stage !== 'done' && (
        <>
          <div className="cim-hint">
            Restore a backup or write spreadsheet edits back to your collection. Photos never change on import.
          </div>
          <button className="cim-btn cim-btn-import" onClick={() => setStage('entry')}>
            ⬆ IMPORT (.XLSX / .CSV)
          </button>
        </>
      )}

      {/* ── modal ─────────────────────────────────────────────────── */}
      {stage !== 'idle' && stage !== 'done' && (
        <div className="cim-overlay" onClick={stage === 'entry' || stage === 'preview' ? reset : undefined}>
          <div className="cim-modal" onClick={(e) => e.stopPropagation()}>

            {stage === 'entry' && (
              <>
                <h2 className="cim-title">IMPORT COLLECTION</h2>
                <div className="cim-sub">Upload a spreadsheet to add cards or update the ones you own. Nothing changes until you review and confirm.</div>
                <div className="cim-fmt">
                  <div className="cim-fmt-item"><b>Editing your collection?</b> Your own export (above) is a ready-made import file — edit it and upload it back.</div>
                  <div className="cim-fmt-item"><b>Adding cards or migrating from another tracker?</b> Set names don't need to be exact — "2026 Galaxy" finds the right set. Anything we can't match, you'll fix with a couple of dropdowns in the preview, not in your spreadsheet.</div>
                  <div className="cim-fmt-item"><b>The rules:</b> column names must match the template (any order, extras ignored). A column you leave out is left alone; a column you include but leave empty clears that field. Photos, marketplace and sold state never change on import.</div>
                </div>
                {error && <div className="cim-error">{error}</div>}
                <div className="cim-btns">
                  <button className="cim-btn cim-btn-import cim-btn-lg" onClick={() => downloadImportTemplate()}>⬇ DOWNLOAD TEMPLATE (.XLSX)</button>
                  <button className="cim-btn cim-btn-primary cim-btn-lg" onClick={() => fileRef.current && fileRef.current.click()}>CHOOSE FILE…</button>
                  <button className="cim-btn cim-btn-danger cim-btn-lg" onClick={reset}>CANCEL</button>
                </div>
              </>
            )}

            {stage === 'parsing' && (
              <>
                <h2 className="cim-title">IMPORT COLLECTION</h2>
                <div className="cim-sub">◐ Reading your file…</div>
              </>
            )}

            {stage === 'preview' && plan && (
              <>
                <h2 className="cim-title">IMPORT PREVIEW</h2>
                <div className="cim-sub">
                  {plan.fileName} · {c.new + c.update + c.unchanged + c.error} row{c.new + c.update + c.unchanged + c.error === 1 ? '' : 's'} read
                </div>

                <div className="cim-chips">
                  <div className="cim-chip cim-chip-new" style={{ opacity: c.new ? 1 : 0.4 }}><b>{c.new}</b><span>NEW</span></div>
                  <div className="cim-chip cim-chip-upd" style={{ opacity: c.update ? 1 : 0.4 }}><b>{c.update}</b><span>UPDATED</span></div>
                  <div className="cim-chip cim-chip-same" style={{ opacity: c.unchanged ? 1 : 0.4 }}><b>{c.unchanged}</b><span>UNCHANGED</span></div>
                  {c.warning > 0 && <div className="cim-chip cim-chip-warn"><b>{c.warning}</b><span>{c.warning === 1 ? 'WARNING' : 'WARNINGS'}</span></div>}
                  {c.error > 0 && <div className="cim-chip cim-chip-err"><b>{c.error}</b><span>TO FIX</span></div>}
                </div>

                {c.error > 0 && (
                  <details className="cim-panel" open>
                    <summary>
                      <span className="cim-red">{c.error} row{c.error === 1 ? '' : 's'} to fix</span>
                      <span className="cim-sum-sub"> — resolve below, or they're skipped on import</span>
                    </summary>
                    <div className="cim-rows">
                      {plan.errors.map((e2) => {
                        const id = errId(e2);
                        const d = fixDrafts[id] || {};
                        const sd = d.setId ? sessionRef.current.setData[d.setId] : null;
                        const sub = sd ? (sd.sets || []).find((s) => s.id === d.subId) : null;
                        const parallels = sd && sub && sub.type !== 'sketch' ? parallelsForSubset(sd, sub) : [];
                        const statusFixable = e2.errType === 'value' && /^Status/.test(e2.reason);
                        const fld = (label, select) => (
                          <div className="cim-fld"><span className="cim-fld-lbl">{label}</span>{select}</div>
                        );
                        return (
                          <div key={id} className="cim-err-card">
                            <div className="cim-err-head">
                              <span className="cim-err-badge">ROW {e2.rowNum}</span>
                              <span className="cim-err-tab">{e2.sheet}</span>
                            </div>
                            <div className="cim-err-msg">{e2.reason}</div>

                            {e2.errType === 'identity' && e2.candidates && e2.candidates.length > 0 && !d.setId && (
                              <div className="cim-cands">
                                {e2.candidates.map((cd) => (
                                  <button key={cd.id} className="cim-cand" onClick={() => pickSet(id, cd.id)}>{cd.name}</button>
                                ))}
                              </div>
                            )}

                            {statusFixable && (
                              <div className="cim-fix">
                                {fld('STATUS', (
                                  <select className="cim-select" defaultValue="" onChange={(ev) => applyStatusFix(e2, ev.target.value)}>
                                    <option value="" disabled>choose…</option>
                                    {VALID_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                ))}
                              </div>
                            )}

                            {e2.errType === 'identity' && (
                              <div className="cim-fix">
                                {fld('SET', (
                                  <select className="cim-select" value={d.setId || ''} onChange={(ev) => pickSet(id, ev.target.value)}>
                                    <option value="">{e2.candidates && e2.candidates.length ? 'or pick any set…' : 'pick the set…'}</option>
                                    {SET_REGISTRY.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                  </select>
                                ))}
                                {d.setId && !d.loading && sd && fld('SUBSET', (
                                  <select className="cim-select" value={d.subId || ''} onChange={(ev) => setDraft(id, { subId: ev.target.value, cardN: '', parallel: '' })}>
                                    <option value="">choose…</option>
                                    {(sd.sets || []).map((s) => <option key={s.id} value={s.id}>{s.title || s.id}</option>)}
                                  </select>
                                ))}
                                {sub && fld('CARD', (
                                  <select className="cim-select" value={d.cardN || ''} onChange={(ev) => setDraft(id, { cardN: ev.target.value })}>
                                    <option value="">choose…</option>
                                    {(sub.cards || []).map((cd) => <option key={cd.n} value={cd.n}>#{cd.n} {cd.name}</option>)}
                                  </select>
                                ))}
                                {sub && parallels.length > 0 && fld('PARALLEL', (
                                  <select className="cim-select" value={d.parallel || ''} onChange={(ev) => setDraft(id, { parallel: ev.target.value })}>
                                    <option value="">none (base)</option>
                                    {parallels.map((p) => <option key={p.id} value={p.name}>{p.name}{p.print_run ? ` /${p.print_run}` : ''}</option>)}
                                  </select>
                                ))}
                                {d.loading && <span className="cim-rn">◐ loading…</span>}
                                {d.setId && (
                                  <button className="cim-btn cim-btn-primary cim-fix-apply" disabled={!d.setId || !d.subId || !d.cardN} onClick={() => applyFix(e2)}>
                                    APPLY FIX
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="cim-hint" style={{ margin: '8px 0 0' }}>
                        Fixed rows move out of this list instantly — nothing is written until you confirm.
                      </div>
                    </div>
                  </details>
                )}

                {c.warning > 0 && (
                  <details className="cim-panel">
                    <summary>
                      <span className="cim-amber">{c.warning} warning{c.warning === 1 ? '' : 's'}</span>
                      <span className="cim-sum-sub"> — imported, but worth a look</span>
                    </summary>
                    <div className="cim-rows">
                      {plan.warnings.map((w, i) => (
                        <div key={i} className="cim-row cim-row-w">
                          <span className="cim-rn">Row {w.rowNum} · "{w.sheet}" tab</span> — {w.message}
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {c.update > 0 && (
                  <details className="cim-panel" open>
                    <summary>
                      <span className="cim-gold">{c.update} update{c.update === 1 ? '' : 's'}</span>
                      <span className="cim-sum-sub"> — sample of what changes</span>
                    </summary>
                    <div className="cim-rows">
                      {sampleChanges.map((r, i) => (
                        <div key={i} className="cim-diff">
                          <div className="cim-diff-key">{r.key}</div>
                          {r.changes.map((ch, j) => (
                            <div key={j} className="cim-diff-line">
                              {ch.header}: <span className="cim-old">{fmtVal(ch.from)}</span>
                              <span className="cim-arrow">→</span>
                              <span className="cim-new">{fmtVal(ch.to)}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                      {c.update > sampleChanges.length && (
                        <div className="cim-hint" style={{ marginTop: 8 }}>…and {c.update - sampleChanges.length} more. Columns not in your file are never touched.</div>
                      )}
                    </div>
                  </details>
                )}

                {plan.notes.length > 0 && (
                  <details className="cim-notes-box">
                    <summary>{plan.notes.length} file note{plan.notes.length === 1 ? '' : 's'} (skipped tabs, ignored columns)</summary>
                    <div className="cim-notes">{plan.notes.map((n, i) => <div key={i}>· {n}</div>)}</div>
                  </details>
                )}

                {error && <div className="cim-error">{error}</div>}

                <div className="cim-confirm-copy">
                  ⚠ Undo removes newly added cards; edits to existing cards stay. Nothing is written until you confirm.
                </div>
                <div className="cim-btns">
                  <button
                    className={`cim-btn cim-btn-lg ${writeCount === 0 ? 'cim-btn-muted' : 'cim-btn-confirm'}`}
                    onClick={confirm}
                    disabled={writeCount === 0}
                  >
                    {writeCount === 0 ? 'NOTHING TO IMPORT YET' : `CONFIRM IMPORT — ${writeCount} ROW${writeCount === 1 ? '' : 'S'}`}
                  </button>
                  <button className="cim-btn cim-btn-danger cim-btn-lg" onClick={reset}>CANCEL</button>
                </div>
              </>
            )}

            {stage === 'writing' && (
              <>
                <h2 className="cim-title">IMPORTING…</h2>
                <div className="cim-prog-lbl">{progress.written} / {progress.total} rows written</div>
                <div className="cim-prog-bar">
                  <div className="cim-prog-fill" style={{ width: `${progress.total ? Math.round((progress.written / progress.total) * 100) : 0}%` }} />
                </div>
                {chunkFail ? (
                  <div className="cim-error" style={{ marginTop: 14 }}>
                    ✕ Import stopped after {chunkFail.written} of {chunkFail.total} rows were written. Those rows are in your collection; nothing was half-written.
                    <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                      <button className="cim-btn cim-btn-primary" onClick={retry}>RETRY REMAINING</button>
                      <button className="cim-btn cim-btn-danger" onClick={reset}>CLOSE</button>
                    </div>
                  </div>
                ) : (
                  <div className="cim-hint" style={{ marginTop: 12 }}>Don't close this tab — almost done.</div>
                )}
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
  .cim-sep { margin: 12px 0; border-top: 1px dashed #1a2540; }
  .cim-hint { font-family: 'Share Tech Mono', monospace; font-size: 11px; color: #7a8aaa; line-height: 1.5; margin-bottom: 10px; }
  .cim-btn { font-family: 'Orbitron', monospace; font-size: 9px; letter-spacing: 1px; border-radius: 2px; padding: 7px 14px; cursor: pointer; background: transparent; }
  .cim-btn:disabled { opacity: 0.55; cursor: default; }
  .cim-btn-lg { font-size: 10px; padding: 10px 20px; }
  .cim-btn-import { color: #b060ff; background: rgba(176,96,255,0.08); border: 1px solid rgba(176,96,255,0.45); }
  .cim-btn-primary { font-weight: 700; color: #041018; background: #00d4ff; border: 1px solid #00d4ff; }
  .cim-btn-confirm { font-weight: 700; color: #041018; background: #4ade80; border: 1px solid #4ade80; }
  .cim-btn-danger { color: #f87171; background: rgba(248,113,113,0.06); border: 1px solid rgba(248,113,113,0.45); }
  .cim-link { font-family: inherit; font-size: inherit; color: #00d4ff; background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; }

  .cim-overlay { position: fixed; inset: 0; background: rgba(2,4,10,0.82); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; overflow-y: auto; padding: 40px 16px; }
  .cim-modal { background: #0c1226; border: 1px solid rgba(0,212,255,0.35); border-radius: 4px; padding: 22px; box-shadow: 0 0 40px rgba(0,0,0,0.6); width: 100%; max-width: 680px; }
  .cim-title { font-family: 'Orbitron', monospace; font-size: 13px; letter-spacing: 2px; color: #e8eef7; margin: 0 0 4px; }
  .cim-sub { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #8a9bb8; margin-bottom: 16px; line-height: 1.5; }

  .cim-fmt { border: 0.5px solid rgba(176,96,255,0.3); border-radius: 3px; background: rgba(176,96,255,0.04); padding: 10px 12px; margin-bottom: 14px; }
  .cim-fmt-item { font-family: 'Share Tech Mono', monospace; font-size: 11px; color: #a8b8d0; line-height: 1.6; margin-bottom: 9px; }
  .cim-fmt-item:last-child { margin-bottom: 0; }
  .cim-fmt-item b { color: #e8eef7; font-weight: 400; }
  .cim-fmt-item code { color: #e8eef7; background: rgba(255,255,255,0.06); padding: 0 4px; border-radius: 2px; font-size: 10px; }

  .cim-chips { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
  .cim-chip { font-family: 'JetBrains Mono', monospace; padding: 8px 12px; border-radius: 3px; border: 0.5px solid; display: flex; flex-direction: column; min-width: 80px; }
  .cim-chip b { font-family: 'Orbitron', monospace; font-size: 18px; font-weight: 700; }
  .cim-chip span { font-size: 9px; letter-spacing: 1px; margin-top: 2px; color: #8a9bb8; }
  .cim-chip-new { border-color: rgba(0,212,255,0.45); background: rgba(0,212,255,0.07); } .cim-chip-new b { color: #00d4ff; }
  .cim-chip-upd { border-color: rgba(240,192,64,0.45); background: rgba(240,192,64,0.07); } .cim-chip-upd b { color: #f0c040; }
  .cim-chip-same { border-color: rgba(138,155,184,0.35); background: rgba(138,155,184,0.05); } .cim-chip-same b { color: #8a9bb8; }
  .cim-chip-warn { border-color: rgba(251,146,60,0.45); background: rgba(251,146,60,0.07); } .cim-chip-warn b { color: #fb923c; }
  .cim-chip-err { border-color: rgba(248,113,113,0.5); background: rgba(248,113,113,0.07); } .cim-chip-err b { color: #f87171; }

  .cim-panel { margin-bottom: 12px; border: 0.5px solid #1a2540; border-radius: 3px; background: rgba(255,255,255,0.02); }
  .cim-panel summary { font-family: 'Share Tech Mono', monospace; font-size: 11px; padding: 10px 12px; cursor: pointer; color: #c8d8f0; }
  .cim-red { color: #f87171; } .cim-amber { color: #fb923c; } .cim-gold { color: #f0c040; }
  .cim-rows { padding: 0 12px 12px; max-height: 260px; overflow-y: auto; }
  .cim-row { font-family: 'JetBrains Mono', monospace; font-size: 11px; line-height: 1.55; padding: 7px 10px; margin-bottom: 6px; border-radius: 2px; border-left: 2px solid; }
  .cim-rn { color: #8a9bb8; }
  .cim-row-e { background: rgba(248,113,113,0.06); border-color: rgba(248,113,113,0.6); color: #f0b0b0; }
  .cim-sum-sub { color: #8a9bb8; }

  .cim-err-card { background: rgba(248,113,113,0.05); border: 0.5px solid rgba(248,113,113,0.3); border-left: 2px solid rgba(248,113,113,0.6); border-radius: 3px; padding: 10px 12px; margin-bottom: 8px; }
  .cim-err-head { display: flex; gap: 8px; align-items: center; margin-bottom: 6px; }
  .cim-err-badge { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1px; color: #f87171; background: rgba(248,113,113,0.12); border: 0.5px solid rgba(248,113,113,0.4); border-radius: 2px; padding: 2px 6px; }
  .cim-err-tab { font-family: 'Share Tech Mono', monospace; font-size: 10px; color: #8a9bb8; }
  .cim-err-msg { font-family: 'JetBrains Mono', monospace; font-size: 11px; line-height: 1.55; color: #f0b0b0; }

  .cim-cands { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
  .cim-cand { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #00d4ff; background: rgba(0,212,255,0.07); border: 1px solid rgba(0,212,255,0.4); border-radius: 2px; padding: 5px 10px; cursor: pointer; }
  .cim-cand:hover { background: rgba(0,212,255,0.14); }

  .cim-fix { display: flex; gap: 8px; flex-wrap: wrap; align-items: flex-end; margin-top: 10px; }
  .cim-fld { display: flex; flex-direction: column; gap: 3px; }
  .cim-fld-lbl { font-family: 'Share Tech Mono', monospace; font-size: 8px; letter-spacing: 1px; color: #8a9bb8; }
  .cim-select { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #c8d8f0; background: #0a1020; border: 1px solid #1a2540; border-radius: 2px; padding: 6px 8px; max-width: 220px; }
  .cim-fix-apply { align-self: flex-end; }
  .cim-btn-muted { color: #8a9bb8; background: rgba(138,155,184,0.06); border: 1px solid #1a2540; font-weight: 400; }
  .cim-row-w { background: rgba(251,146,60,0.06); border-color: rgba(251,146,60,0.6); color: #f0cfa8; }

  .cim-diff { margin-bottom: 10px; }
  .cim-diff-key { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #e8eef7; margin-bottom: 2px; word-break: break-all; }
  .cim-diff-line { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #8a9bb8; padding-left: 10px; }
  .cim-old { color: #8a9bb8; text-decoration: line-through; text-decoration-color: rgba(248,113,113,0.6); }
  .cim-new { color: #4ade80; }
  .cim-arrow { color: #5a6a8a; padding: 0 5px; }

  .cim-notes-box { margin-bottom: 12px; }
  .cim-notes-box summary { font-family: 'Share Tech Mono', monospace; font-size: 10px; color: #5a6a8a; cursor: pointer; }
  .cim-notes { font-family: 'Share Tech Mono', monospace; font-size: 10px; color: #5a6a8a; line-height: 1.6; padding: 6px 0 0 8px; }
  .cim-error { font-family: 'Share Tech Mono', monospace; font-size: 12px; color: #f87171; border: 1px solid rgba(248,113,113,0.4); border-radius: 2px; padding: 10px 14px; background: rgba(248,113,113,0.08); margin-bottom: 12px; line-height: 1.6; }
  .cim-confirm-copy { font-family: 'Share Tech Mono', monospace; font-size: 11px; color: #f0cfa8; background: rgba(251,146,60,0.06); border: 0.5px solid rgba(251,146,60,0.35); border-radius: 3px; padding: 10px 12px; line-height: 1.6; margin: 14px 0; }
  .cim-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 6px; }

  .cim-prog-lbl { font-family: 'Share Tech Mono', monospace; font-size: 11px; color: #8a9bb8; margin-bottom: 6px; }
  .cim-prog-bar { height: 8px; background: rgba(255,255,255,0.05); border: 0.5px solid #1a2540; border-radius: 2px; overflow: hidden; }
  .cim-prog-fill { height: 100%; background: linear-gradient(90deg, #00d4ff, #b060ff); transition: width 0.2s; }

  .cim-done { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-family: 'Share Tech Mono', monospace; font-size: 12px; color: #4ade80; background: rgba(74,222,128,0.07); border: 0.5px solid rgba(74,222,128,0.4); border-radius: 3px; padding: 12px 14px; }
`;
